"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Link from "next/link";
import { ChevronDown, Menu, X, ChevronRight } from "lucide-react";

type Child    = { id: string; title: string; slug: string };
type MenuItem = { id: string; title: string; slug: string; children: Child[] };
type StaticLink = { label: string; href: string };

export const STATIC_LINKS: StaticLink[] = [
  { label: "Actualités", href: "/actualites" },
  { label: "Agenda",     href: "/agenda" },
];

/* ─── Desktop nav (ligne 2 du header) ─────────────────────────────────────── */
export function DesktopNav({ items }: { items: MenuItem[] }) {
  const [openId, setOpenId]   = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enter = useCallback((id: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenId(id);
  }, []);

  const leave = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpenId(null), 130);
  }, []);

  return (
    <nav className="flex flex-wrap items-center gap-x-1 gap-y-0">
      {items.map((item) =>
        item.children.length === 0 ? (
          <DesktopLink key={item.id} href={`/${item.slug}`}>{item.title}</DesktopLink>
        ) : (
          <div key={item.id} className="relative" onMouseEnter={() => enter(item.id)} onMouseLeave={leave}>
            <button
              type="button"
              className={[
                "flex items-center gap-1.5 rounded-lg px-4 py-4 text-[15px] font-semibold transition-all duration-150",
                openId === item.id ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10 hover:text-white",
              ].join(" ")}
            >
              {item.title}
              <ChevronDown size={13} className={["transition-transform duration-200", openId === item.id ? "rotate-180" : ""].join(" ")} />
            </button>

            {/* Dropdown */}
            <div
              onMouseEnter={() => enter(item.id)}
              onMouseLeave={leave}
              className={[
                "absolute left-0 top-full z-50 w-max overflow-hidden rounded-2xl shadow-2xl transition-all duration-200 origin-top-left",
                openId === item.id ? "opacity-100 scale-100 pointer-events-auto translate-y-0" : "opacity-0 scale-95 pointer-events-none -translate-y-1",
              ].join(" ")}
              style={{ background: "linear-gradient(160deg, #1d2e50 0%, #243757 100%)", border: "1px solid rgba(255,255,255,0.12)", minWidth: "200px" }}
            >
              <div className="border-b border-white/10 px-4 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/40">{item.title}</p>
              </div>
              <div
                className="p-1.5"
                style={{
                  display: "grid",
                  gridTemplateColumns: item.children.length > 12 ? "repeat(3,auto)" : item.children.length > 6 ? "repeat(2,auto)" : "1fr",
                  gap: "1px",
                }}
              >
                {item.children.map((child) => (
                  <Link
                    key={child.id}
                    href={`/${child.slug}`}
                    onClick={() => setOpenId(null)}
                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-white/70 transition hover:bg-white/10 hover:text-white whitespace-nowrap"
                  >
                    <ChevronRight size={11} className="shrink-0 text-white/25" />
                    {child.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )
      )}

      {STATIC_LINKS.map((l) => (
        <DesktopLink key={l.href} href={l.href}>{l.label}</DesktopLink>
      ))}
    </nav>
  );
}

function DesktopLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="rounded-lg px-4 py-4 text-[15px] font-semibold text-white/80 transition-all duration-150 hover:bg-white/10 hover:text-white"
    >
      {children}
    </Link>
  );
}

/* ─── Mobile burger + drawer ───────────────────────────────────────────────── */
export default function MegaMenu({ items }: { items: MenuItem[] }) {
  const [mounted, setMounted]       = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobile, setOpenMobile] = useState<string | null>(null);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const toggleMobile = (id: string) => setOpenMobile((p) => (p === id ? null : id));
  const closeAll = () => { setMobileOpen(false); setOpenMobile(null); };

  return (
    <>
      {/* Burger — mobile uniquement */}
      <button
        type="button"
        className="lg:hidden flex h-10 w-10 items-center justify-center rounded-xl text-white/80 transition hover:bg-white/10 hover:text-white"
        onClick={() => setMobileOpen(true)}
        aria-label="Menu"
      >
        <Menu size={22} />
      </button>

      {mounted && (
        <>
          {/* Overlay */}
          <div
            className={["fixed inset-0 z-[90] lg:hidden bg-black/60 transition-opacity duration-300",
              mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"].join(" ")}
            onClick={closeAll}
          />

          {/* Drawer */}
          <div
            className={["fixed inset-y-0 right-0 z-[100] lg:hidden w-[85vw] max-w-sm flex flex-col",
              "transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]",
              mobileOpen ? "translate-x-0" : "translate-x-full"].join(" ")}
            style={{ background: "linear-gradient(180deg,#1a2744 0%,#1e3a5f 100%)" }}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <span className="text-base font-bold text-white">Navigation</span>
              <button type="button" onClick={closeAll}
                className="flex h-8 w-8 items-center justify-center rounded-xl text-white/50 hover:bg-white/10 hover:text-white transition">
                <X size={18} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-3">
              <MobileLink href="/" onClick={closeAll}>Accueil</MobileLink>

              {items.map((item) => (
                <div key={item.id}>
                  {item.children.length === 0 ? (
                    <MobileLink href={`/${item.slug}`} onClick={closeAll}>{item.title}</MobileLink>
                  ) : (
                    <div className="mb-0.5">
                      <button type="button" onClick={() => toggleMobile(item.id)}
                        className={["flex w-full items-center justify-between rounded-2xl px-4 py-3 text-sm font-semibold transition",
                          openMobile === item.id ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"].join(" ")}>
                        <span>{item.title}</span>
                        <ChevronDown size={15} className={["transition-transform duration-300", openMobile === item.id ? "rotate-180" : ""].join(" ")} />
                      </button>

                      <div className="overflow-hidden transition-all duration-300"
                        style={{ maxHeight: openMobile === item.id ? `${(item.children.length + 1) * 48}px` : "0px" }}>
                        <div className="mx-2 mb-1 mt-0.5 overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                          {item.children.map((child, i) => (
                            <Link key={child.id} href={`/${child.slug}`} onClick={closeAll}
                              className={["flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition",
                                i < item.children.length - 1 ? "border-b border-white/5" : ""].join(" ")}>
                              <ChevronRight size={12} className="text-white/25" /> {child.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="mx-4 my-2 border-t border-white/10" />

              {STATIC_LINKS.map((l) => (
                <MobileLink key={l.href} href={l.href} onClick={closeAll}>{l.label}</MobileLink>
              ))}
            </nav>

            <div className="border-t border-white/10 px-5 py-3 text-center text-xs text-white/25">
              Courcelles-lès-Lens — Site officiel
            </div>
          </div>
        </>
      )}
    </>
  );
}

function MobileLink({ href, onClick, children }: { href: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Link href={href} onClick={onClick}
      className="flex items-center rounded-2xl px-4 py-3 text-sm font-semibold text-white/80 transition hover:bg-white/10 hover:text-white">
      {children}
    </Link>
  );
}
